import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';

const PrintPromoHeader = ({ logoUrl, logoAltText, navigationLinks, quoteUrl, homeUrl, currentPage }) => {
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const headerRef = useRef(null);
	const lastScrollY = useRef(0);
	const hamburgerRef = useRef(null);
	const mobileNavRef = useRef(null);

	useEffect(() => {
		const handleScroll = () => {
			const currentY = window.scrollY;
			const header = headerRef.current;
			if (!header) return;

			if (currentY < 10) {
				header.classList.remove('nav-up', 'nav-down');
			} else if (currentY > lastScrollY.current) {
				header.classList.add('nav-up');
				header.classList.remove('nav-down');
			} else {
				header.classList.remove('nav-up');
				header.classList.add('nav-down');
			}
			lastScrollY.current = currentY;
		};
		window.addEventListener('scroll', handleScroll, true);
		return () => window.removeEventListener('scroll', handleScroll, true);
	}, []);

	useEffect(() => {
		if (mobileNavOpen) {
			document.body.classList.add('lock');
			const firstFocusable = mobileNavRef.current?.querySelector('a, button');
			firstFocusable?.focus();
		} else {
			document.body.classList.remove('lock');
		}
		return () => document.body.classList.remove('lock');
	}, [mobileNavOpen]);

	useEffect(() => {
		if (!mobileNavOpen) return;

		const handleKeyDown = (e) => {
			if (e.key === 'Escape') {
				setMobileNavOpen(false);
				hamburgerRef.current?.focus();
				return;
			}
			if (e.key !== 'Tab') return;

			const focusable = mobileNavRef.current?.querySelectorAll('a, button');
			if (!focusable || focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];

			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault();
					last.focus();
				}
			} else {
				if (document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [mobileNavOpen]);

	const toggleMobileNav = () => setMobileNavOpen(prev => !prev);
	const closeMobileNav = () => {
		setMobileNavOpen(false);
		hamburgerRef.current?.focus();
	};

	return (
		<>
			<header ref={headerRef} className="header">
				<Box className="container">
					<Box className="logo">
						<a href={homeUrl}>
							<img src={logoUrl} alt={logoAltText} />
						</a>
					</Box>
					<Box className="navigation" component="nav" aria-label="Main navigation">
						<ul>
							{navigationLinks?.map((link, index) => (
								<li key={index}>
									<a href={link.URL} className={currentPage === link.Slug ? 'active' : ''} aria-current={currentPage === link.Slug ? 'page' : undefined}>{link.Text}</a>
								</li>
							))}
						</ul>
					</Box>
					<Box className="cta">
						<a href={quoteUrl} className="btn">{typeof translator !== 'undefined' ? (translator.translate('sandhills.printpromo.requestaquote') || 'Request A Quote') : 'Request A Quote'}</a>
					</Box>
					<Box className="navicon">
						<button
							ref={hamburgerRef}
							onClick={toggleMobileNav}
							aria-label="Open navigation menu"
							aria-expanded={mobileNavOpen}
							aria-controls="mobile-nav"
						>
							<i className="fa-solid fa-bars" aria-hidden="true"></i>
						</button>
					</Box>
				</Box>
			</header>

			<Box
				id="mobile-nav"
				className={`mobile-nav ${mobileNavOpen ? 'active' : ''}`}
				ref={mobileNavRef}
				aria-hidden={!mobileNavOpen}
			>
				<Box className="close">
					<button onClick={closeMobileNav} aria-label="Close navigation menu">
						<i className="fa-solid fa-xmark" aria-hidden="true"></i>
					</button>
				</Box>
				<Box className="navigation" component="nav" aria-label="Mobile navigation">
					<ul>
						<li><a href={homeUrl} onClick={closeMobileNav}>Home</a></li>
						{navigationLinks?.map((link, index) => (
							<li key={index}>
								<a href={link.URL} onClick={closeMobileNav}>{link.Text}</a>
							</li>
						))}
						<li><a href={quoteUrl} onClick={closeMobileNav}>{typeof translator !== 'undefined' ? (translator.translate('sandhills.printpromo.requestaquote') || 'Request A Quote') : 'Request A Quote'}</a></li>
					</ul>
				</Box>
				<Box className="copyright">
					Copyright &copy; {new Date().getFullYear()} Sandhills Global
				</Box>
			</Box>
		</>
	);
};

export default PrintPromoHeader;
